import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Markdown } from "@tiptap/markdown";
import { Extension } from "@tiptap/core";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link2,
  Quote,
  Code,
  SquareCode,
  Type,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  isMarkdownEmpty,
  isAllowedEditorHref,
  normalizeEditorMarkdown,
  sanitizePastedHtml,
} from "@/lib/editorMarkdown";
import {
  shouldSubmitOnEnter,
  promoteMarkdownListLine,
  promoteMarkdownListTriggerOnSpace,
} from "@/lib/composerKeyboard";
import { createReferenceSuggestionsExtension } from "@/lib/referenceSuggestions";

export { isMarkdownEmpty, normalizeEditorMarkdown };

/** Fallback when markdown includes nodes the schema dropped (e.g. headings). */
function markdownToParagraphDoc(markdown) {
  const lines = String(markdown || "").replace(/\r\n/g, "\n").split("\n");
  return {
    type: "doc",
    content: (lines.length ? lines : [""]).map((line) => ({
      type: "paragraph",
      content: line ? [{ type: "text", text: line }] : [],
    })),
  };
}

function applyMarkdownContent(editor, markdown) {
  const next = markdown || "";
  editor.commands.setContent(next, { contentType: "markdown" });
  if (next.trim() && editor.isEmpty) {
    editor.commands.setContent(markdownToParagraphDoc(next));
  }
}

function editorIsInList(editor) {
  if (!editor) return false;
  if (
    editor.isActive("listItem") ||
    editor.isActive("bulletList") ||
    editor.isActive("orderedList")
  ) {
    return true;
  }
  try {
    return !!editor.can?.()?.splitListItem?.("listItem");
  } catch {
    return false;
  }
}

function useModKeyLabel() {
  return useMemo(() => {
    if (typeof navigator === "undefined") return "Ctrl";
    const platform = navigator.platform || "";
    const ua = navigator.userAgent || "";
    if (/Mac|iPhone|iPad|iPod/i.test(platform) || /Mac OS X/i.test(ua)) {
      return "⌘";
    }
    return "Ctrl";
  }, []);
}

function formatShortcut(modKey, parts) {
  if (modKey === "⌘") {
    return [modKey, ...parts].join("");
  }
  return [modKey, ...parts].join("+");
}

function ToolbarButton({ active, disabled, onClick, label, shortcut, children }) {
  const aria = shortcut ? `${label} (${shortcut})` : label;
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={onClick}
          aria-label={aria}
          aria-pressed={!!active}
          className={cn(
            "h-7 w-7 shrink-0 text-muted-foreground",
            active && "bg-muted text-foreground"
          )}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="flex items-center gap-2 px-2 py-1 text-xs"
      >
        <span>{label}</span>
        {shortcut ? (
          <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
            {shortcut}
          </kbd>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}

const NARROW_TOOLBAR_PX = 300;

/**
 * Shared TipTap Markdown composer for DMs, posts, comments, and cards.
 *
 * @param {"message"|"mod-enter"} [submitMode="message"]
 *   - message: desktop Enter submits (DM behavior)
 *   - mod-enter: Enter = newline; Ctrl/Cmd+Enter submits
 * @param {"full"|"compact"} [variant="full"]
 *   compact: slim toolbar (bold/italic/lists/link/code) for Kanban cards
 * @param {"always"|"auto"|"never"} [toolbar="always"]
 *   auto: collapses formatting controls on mobile viewports or when the editor is narrow
 *   never: hide the formatting toolbar (mobile comment composers)
 * @param {"default"|"borderless"} [chrome="default"]
 *   borderless: no outer border/background (pill composers own the chrome)
 */
const RichMarkdownEditor = forwardRef(function RichMarkdownEditor(
  {
    value = "",
    onChange,
    onUserInput,
    onSubmit,
    onEscape,
    /** Return true to consume the key event (e.g. slash-command menu). */
    onEditorKeyDown = null,
    disabled = false,
    placeholder = "Write a message…",
    className,
    editorClassName,
    minRows,
    autoFocus = false,
    submitMode = "message",
    variant = "full",
    toolbar = "always",
    chrome = "default",
    /** When enabled, @user / #channel suggestions are active (posts, comments, DMs). */
    referenceContext = null,
  },
  ref
) {
  const isMobile = useIsMobile();
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  const submitModeRef = useRef(submitMode);
  submitModeRef.current = submitMode;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const onEditorKeyDownRef = useRef(onEditorKeyDown);
  onEditorKeyDownRef.current = onEditorKeyDown;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onUserInputRef = useRef(onUserInput);
  onUserInputRef.current = onUserInput;
  const referenceContextRef = useRef(referenceContext);
  referenceContextRef.current = referenceContext;
  const setLinkRef = useRef(null);

  const isCompact = variant === "compact";
  const resolvedMinRows = minRows ?? (isCompact ? 3 : 2);
  const rootRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const hideToolbar = toolbar === "never";
  // Collapse on phone viewport or when the editor root itself is narrow (drawer).
  const collapseToolbar = toolbar === "auto" && (isMobile || isNarrow);
  const showToolbarButtons = !collapseToolbar || toolbarOpen;

  useEffect(() => {
    if (toolbar !== "auto") {
      setIsNarrow(false);
      setToolbarOpen(false);
      return undefined;
    }
    const node = rootRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const update = (width) => {
      const narrow = width < NARROW_TOOLBAR_PX;
      setIsNarrow(narrow);
      if (!narrow) setToolbarOpen(false);
    };

    update(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      update(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [toolbar]);

  const modKey = useModKeyLabel();
  const shortcuts = useMemo(
    () => ({
      bold: formatShortcut(modKey, ["B"]),
      italic: formatShortcut(modKey, ["I"]),
      underline: formatShortcut(modKey, ["U"]),
      bullet: formatShortcut(modKey, ["Shift", "8"]),
      ordered: formatShortcut(modKey, ["Shift", "7"]),
      link: formatShortcut(modKey, ["K"]),
      quote: formatShortcut(modKey, ["Shift", "B"]),
      code: formatShortcut(modKey, ["E"]),
      codeBlock: formatShortcut(modKey, ["Alt", "C"]),
    }),
    [modKey]
  );

  const lastEmittedRef = useRef(value || "");
  const skipUserInputRef = useRef(false);
  const editorRef = useRef(null);
  const valueRef = useRef(value || "");
  valueRef.current = value || "";

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        horizontalRule: false,
        link: false,
      }),
      Link.extend({
        // Keep autolink for pasted/typed URLs, but don't let typing after a
        // @mention / #channel continue inside the link mark.
        inclusive: false,
      }).configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        protocols: ["http", "https"],
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
        validate: (href) => isAllowedEditorHref(href),
      }),
      Extension.create({
        name: "circaListShortcutAliases",
        addKeyboardShortcuts() {
          return {
            "Mod-Shift-*": () => this.editor.commands.toggleBulletList(),
            "Mod-Shift-&": () => this.editor.commands.toggleOrderedList(),
          };
        },
      }),
      Markdown.configure({
        markedOptions: {
          gfm: true,
          breaks: false,
        },
      }),
      createReferenceSuggestionsExtension(() => {
        const ctx = referenceContextRef.current || {};
        return {
          enabled: Boolean(
            ctx.enabled &&
              ctx.accessToken &&
              (ctx.socialMode || ctx.orgId)
          ),
          orgId: ctx.orgId,
          accessToken: ctx.accessToken,
          conversationId: ctx.conversationId,
          contextType: ctx.contextType,
          onInviteAndMention: ctx.onInviteAndMention,
          onReferenceInserted: ctx.onReferenceInserted,
        };
      }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: value || "",
    contentType: "markdown",
    editable: !disabled,
    autofocus: autoFocus ? "end" : false,
    immediatelyRender: false,
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        // text-base below md avoids iOS Safari focus-zoom (must be ≥16px).
        class: cn(
          "outline-none max-w-none leading-snug text-base md:text-sm",
          "[&_p]:my-0.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
          "[&_li]:my-0.5",
          "[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
          "[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-2 [&_pre]:text-xs",
          "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs",
          "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
          "[&_a]:text-primary [&_a]:underline",
          "[&_u]:underline",
          editorClassName
        ),
        spellcheck: "true",
        autocapitalize: "sentences",
        autocorrect: "on",
        autocomplete: "on",
        "data-placeholder": placeholder,
      },
      transformPastedHTML(html) {
        return sanitizePastedHtml(html);
      },
      handleKeyDown(_view, event) {
        if (onEditorKeyDownRef.current?.(event)) {
          return true;
        }
        if (event.key === "Escape") {
          if (onEscapeRef.current) {
            onEscapeRef.current();
            return true;
          }
          return false;
        }
        if (
          (event.key === "k" || event.key === "K") &&
          (event.metaKey || event.ctrlKey) &&
          !event.altKey &&
          !event.shiftKey
        ) {
          event.preventDefault();
          setLinkRef.current?.();
          return true;
        }

        // Slack-style: "1." / "-" + Space starts a list (compose + edit).
        // TipTap's built-in wrapping input rules are unreliable here once
        // Enter-to-send and suggestion plugins are in the stack.
        if (
          event.key === " " &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey &&
          !event.isComposing
        ) {
          const ed = editorRef.current;
          if (promoteMarkdownListTriggerOnSpace(ed)) {
            event.preventDefault();
            return true;
          }
          return false;
        }

        if (event.key !== "Enter") return false;
        if (event.isComposing || event.keyCode === 229) return false;
        if (event.shiftKey) return false;

        const ed = editorRef.current;
        const plainEnterSubmits = submitModeRef.current === "message";
        const isModEnter = !!(event.ctrlKey || event.metaKey);

        // Ctrl/Cmd+Enter always submits when a handler is provided, even inside
        // lists / code blocks (posts, comments, cards, and DMs).
        if (isModEnter) {
          if (
            shouldSubmitOnEnter(event, {
              isMobile: isMobileRef.current,
              plainEnterSubmits,
            })
          ) {
            onSubmitRef.current?.();
            return true;
          }
          return false;
        }

        if (ed?.isActive("codeBlock")) return false;
        if (editorIsInList(ed)) return false;

        if (promoteMarkdownListLine(ed)) {
          return true;
        }

        const submit = shouldSubmitOnEnter(event, {
          isMobile: isMobileRef.current,
          inCodeBlock: false,
          inListItem: false,
          plainEnterSubmits,
        });
        if (!submit) return false;
        onSubmitRef.current?.();
        return true;
      },
    },
    onCreate: ({ editor: ed }) => {
      editorRef.current = ed;
      lastEmittedRef.current = ed.getMarkdown?.() ?? valueRef.current ?? "";
    },
    onUpdate: ({ editor: ed, transaction }) => {
      const md = ed.getMarkdown?.() ?? "";
      // Always notify when the doc markdown actually changed. Programmatic
      // setContent sets lastEmitted after apply so this is a no-op then.
      if (md === lastEmittedRef.current) return;
      lastEmittedRef.current = md;
      onChangeRef.current?.(md);
      if (transaction?.docChanged && !skipUserInputRef.current) {
        onUserInputRef.current?.();
      }
      skipUserInputRef.current = false;
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next === lastEmittedRef.current) return;

    // Controlled value lagged behind the editor. Push editor markdown up
    // instead of wiping visible text with "".
    if (
      next === "" &&
      lastEmittedRef.current &&
      !isMarkdownEmpty(lastEmittedRef.current)
    ) {
      onChangeRef.current?.(lastEmittedRef.current);
      return;
    }

    skipUserInputRef.current = true;
    applyMarkdownContent(editor, next);
    lastEmittedRef.current = editor.getMarkdown?.() ?? next;
    skipUserInputRef.current = false;
  }, [editor, value]);

  useImperativeHandle(
    ref,
    () => ({
      focus: (pos = "end") => {
        editor?.commands.focus(pos);
      },
      blur: () => {
        editor?.commands.blur();
      },
      clear: () => {
        if (!editor) return;
        skipUserInputRef.current = true;
        editor.commands.clearContent(true);
        lastEmittedRef.current = "";
        skipUserInputRef.current = false;
      },
      insertText: (text) => {
        if (!editor || text == null) return;
        editor.chain().focus().insertContent(String(text)).run();
      },
      getMarkdown: () => editor?.getMarkdown?.() ?? "",
      isEmpty: () => {
        if (!editor) return true;
        if (editor.isEmpty) return true;
        return isMarkdownEmpty(editor.getMarkdown?.() ?? "");
      },
      setMarkdown: (md) => {
        if (!editor) return;
        skipUserInputRef.current = true;
        applyMarkdownContent(editor, md || "");
        lastEmittedRef.current = editor.getMarkdown?.() ?? md ?? "";
        skipUserInputRef.current = false;
      },
      editor,
    }),
    [editor]
  );

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const next = window.prompt("Link URL", prev || "https://");
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    if (!isAllowedEditorHref(trimmed)) return;
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: trimmed })
      .run();
  }, [editor]);

  setLinkRef.current = setLink;

  const minHeight = Math.max(1, resolvedMinRows) * 1.375;

  const toolbarButtons = (
    <>
      <ToolbarButton
        label="Bold"
        shortcut={shortcuts.bold}
        disabled={disabled || !editor}
        active={editor?.isActive("bold")}
        onClick={() => editor?.chain().focus().toggleBold().run()}
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        shortcut={shortcuts.italic}
        disabled={disabled || !editor}
        active={editor?.isActive("italic")}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      {!isCompact && (
        <ToolbarButton
          label="Underline"
          shortcut={shortcuts.underline}
          disabled={disabled || !editor}
          active={editor?.isActive("underline")}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
      )}
      <ToolbarButton
        label="Bulleted list"
        shortcut={shortcuts.bullet}
        disabled={disabled || !editor}
        active={editor?.isActive("bulletList")}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        shortcut={shortcuts.ordered}
        disabled={disabled || !editor}
        active={editor?.isActive("orderedList")}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        label="Link"
        shortcut={shortcuts.link}
        disabled={disabled || !editor}
        active={editor?.isActive("link")}
        onClick={setLink}
      >
        <Link2 className="h-3.5 w-3.5" />
      </ToolbarButton>
      {!isCompact && (
        <ToolbarButton
          label="Quote"
          shortcut={shortcuts.quote}
          disabled={disabled || !editor}
          active={editor?.isActive("blockquote")}
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="h-3.5 w-3.5" />
        </ToolbarButton>
      )}
      <ToolbarButton
        label="Inline code"
        shortcut={shortcuts.code}
        disabled={disabled || !editor}
        active={editor?.isActive("code")}
        onClick={() => editor?.chain().focus().toggleCode().run()}
      >
        <Code className="h-3.5 w-3.5" />
      </ToolbarButton>
      {!isCompact && (
        <ToolbarButton
          label="Code block"
          shortcut={shortcuts.codeBlock}
          disabled={disabled || !editor}
          active={editor?.isActive("codeBlock")}
          onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
        >
          <SquareCode className="h-3.5 w-3.5" />
        </ToolbarButton>
      )}
    </>
  );

  const borderless = chrome === "borderless";

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={rootRef}
        className={cn(
          "flex min-w-0 flex-1 flex-col",
          borderless
            ? "bg-transparent"
            : "rounded-md border border-input bg-background",
          disabled && "opacity-50 pointer-events-none",
          className
        )}
      >
        {hideToolbar ? null : collapseToolbar ? (
          <div
            className={cn(
              "flex items-center gap-0.5 px-1 py-0.5",
              !borderless && "border-b border-border"
            )}
          >
            <ToolbarButton
              label={toolbarOpen ? "Hide formatting" : "Formatting"}
              disabled={disabled}
              active={toolbarOpen}
              onClick={() => setToolbarOpen((open) => !open)}
            >
              {toolbarOpen ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <Type className="h-3.5 w-3.5" />
              )}
            </ToolbarButton>
            {showToolbarButtons ? (
              <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto scrollbar-hide">
                {toolbarButtons}
              </div>
            ) : null}
          </div>
        ) : (
          <div
            className={cn(
              "flex flex-nowrap items-center gap-0.5 overflow-x-auto px-1 py-0.5 scrollbar-hide",
              !borderless && "border-b border-border"
            )}
          >
            {toolbarButtons}
          </div>
        )}
        <div
          className={cn(
            "relative min-h-0 flex-1",
            borderless ? "px-1 py-1" : "px-2 py-1.5"
          )}
          style={{ minHeight: `${minHeight}rem` }}
          onClick={() => {
            if (!disabled) editor?.commands.focus();
          }}
        >
          <EditorContent editor={editor} />
          {editor?.isEmpty && (
            <div
              className={cn(
                "pointer-events-none absolute text-muted-foreground text-base md:text-sm",
                borderless ? "left-1 top-1" : "left-2 top-1.5"
              )}
            >
              {placeholder}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
});

export default RichMarkdownEditor;
